import { useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface Execution {
  readonly executionId: string;
  /** The report the execution ran — an execution has no number of its own. */
  readonly reportName: string | null;
  readonly executionStatus: string;
  readonly startedAt: string | null;
}

interface ExportResult {
  readonly exportId: string;
  readonly exportFormat: string;
}

const EXPORT_FORMATS = ['PDF', 'EXCEL', 'CSV', 'WORD', 'JSON', 'XML'] as const;

/** L16.11 — Export Center (`/reports/export`). Exports a completed report
 *  execution in a chosen format and records the download (M2). */
export function M16ExportCenterPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [executionId, setExecutionId] = useState('');
  const [exportFormat, setExportFormat] = useState<string>('PDF');
  const [result, setResult] = useState<ExportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState(false);

  async function runExport(): Promise<void> {
    setBusy(true);
    setError(null);
    setResult(null);
    setDownloaded(false);
    try {
      const res = await apiRequest<ExportResult>('/v1/m16/reports/export', {
        method: 'POST',
        token,
        body: { executionId, exportFormat },
      });
      setResult(res);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function download(id: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`/v1/m16/reports/exports/${id}/download`, { method: 'POST', token });
      setDownloaded(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main aria-label="export-center">
      <h1>{t('m16.export.title')}</h1>
      <section aria-label="export-form">
        <RecordPicker<Execution>
          endpoint="/v1/m16/reports/executions"
          idField="executionId"
          label={t('m16.export.placeholderExecId')}
          testId="export-execution-id"
          value={executionId}
          optionLabel={(e) =>
            [e.reportName,
             e.startedAt ? at(e.startedAt) : null,
             e.executionStatus].filter(Boolean).join(' · ')
          }
          onPick={setExecutionId}
        />
        <select aria-label="export-format" data-testid="export-format"
          value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
          {EXPORT_FORMATS.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <button type="button" data-testid="export-btn" disabled={busy || !executionId} onClick={runExport}>{t('m16.export.exportBtn')}</button>
      </section>
      {error && <p role="alert">{error}</p>}
      {result && (
        <div aria-label="export-result">
          <p>{t('m16.export.resultLabel')} <strong>{result.exportFormat}</strong></p>
          <button type="button" data-testid="download-btn" disabled={busy}
            onClick={() => download(result.exportId)}>{t('m16.export.downloadBtn')}</button>
          {downloaded && <p data-testid="download-confirmed">{t('m16.export.downloadRecorded')}</p>}
        </div>
      )}
    </main>
  );
}
